import { App, Modal, Skeleton } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractErrorMessage, httpClient } from '@shared/api';
import { TopBar, useUnsavedGuard } from '@shared/ui/v2';
import './account.css';

interface Account {
  email: string | null;
  display_name: string | null;
  company_name: string | null;
  company_inn: string | null;
  low_balance_threshold: string;
  notify_low_balance: boolean;
  notify_model_replaced: boolean;
  joined_at: string;
  has_password: boolean;
}

type Form = {
  display_name: string;
  company_name: string;
  company_inn: string;
  low_balance_threshold: string;
  notify_low_balance: boolean;
  notify_model_replaced: boolean;
};

const KEY = ['account'];
const SECTIONS = [
  ['profile', 'Профиль'],
  ['company', 'Компания и документы'],
  ['notifications', 'Уведомления'],
  ['security', 'Безопасность'],
] as const;

const toForm = (a: Account): Form => ({
  display_name: a.display_name ?? '',
  company_name: a.company_name ?? '',
  company_inn: a.company_inn ?? '',
  low_balance_threshold: String(Number(a.low_balance_threshold)).replace('.', ','),
  notify_low_balance: a.notify_low_balance,
  notify_model_replaced: a.notify_model_replaced,
});

/**
 * Экран 15 «Настройки» (утверждён 28.10.2026). Сохраняется само.
 * P2 по фич-листу (тёмная тема, уведомления в Telegram, отчёт за месяц) — до готовности
 * не показываем.
 */
export function AccountPage() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: acc, error } = useQuery<Account, Error>({
    queryKey: KEY,
    queryFn: async () => (await httpClient.get<Account>('/account')).data,
  });
  const [form, setForm] = useState<Form | null>(null);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errField, setErrField] = useState<string | null>(null);
  const saved = useRef<string>('');
  const [active, setActive] = useState<string>(
    () => window.location.hash.replace('#', '') || 'profile',
  );

  useEffect(() => {
    if (acc && !form) {
      const f = toForm(acc);
      saved.current = JSON.stringify(f);
      setForm(f);
    }
  }, [acc, form]);

  // К разделу по ссылке (#notifications со страницы «Баланс и расход») — один раз, при загрузке.
  const hasForm = !!form;
  useEffect(() => {
    if (!hasForm) return;
    const id = window.location.hash.replace('#', '');
    if (id) document.getElementById(`sec-${id}`)?.scrollIntoView({ block: 'start' });
  }, [hasForm]);

  const sig = form ? JSON.stringify(form) : '';
  // Что можно сохранить сейчас: поле с ошибкой (ИНН, порог) не отправляем, остальное — да.
  const payload = (f: Form) => {
    const inn = f.company_inn.replace(/\s/g, '');
    const innOk = !inn || /^(\d{10}|\d{12})$/.test(inn);
    const thrRaw = f.low_balance_threshold.replace(/\s/g, '').replace(',', '.');
    const thr = Number(thrRaw);
    const thrOk = thrRaw !== '' && Number.isFinite(thr) && thr >= 0 && thr <= 100000;
    return {
      bad: !innOk ? 'inn' : !thrOk ? 'threshold' : null,
      body: {
        display_name: f.display_name,
        company_name: f.company_name,
        ...(innOk ? { company_inn: inn } : {}),
        ...(thrOk ? { low_balance_threshold: thr } : {}),
        notify_low_balance: f.notify_low_balance,
        notify_model_replaced: f.notify_model_replaced,
      },
    };
  };
  // Уход со страницы раньше, чем сработал таймер: отправляем несохранённое.
  const pending = useRef<null | (() => void)>(null);
  pending.current =
    form && saved.current && sig !== saved.current
      ? () => {
          httpClient
            .patch('/account', payload(form).body)
            .then(() => qc.invalidateQueries({ queryKey: KEY }))
            .catch(() => undefined);
        }
      : null;
  useEffect(() => () => pending.current?.(), []);
  useUnsavedGuard(state === 'error', 'Последние изменения настроек не сохранились.');

  useEffect(() => {
    if (!form || !saved.current || sig === saved.current) return undefined;
    const { bad, body } = payload(form);
    setErrField(bad);
    const t = setTimeout(async () => {
      setState('saving');
      try {
        const next = (await httpClient.patch<Account>('/account', body)).data;
        saved.current = sig;
        qc.setQueryData(KEY, next);
        qc.invalidateQueries({ queryKey: ['billing-v2', 'summary'] });
        setState('saved');
      } catch (e) {
        setState('error');
        message.error(extractErrorMessage(e));
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (error)
    return (
      <div className="v2-content">
        <div className="v2-card v2-card-pad">
          Не удалось загрузить настройки: {extractErrorMessage(error)}
        </div>
      </div>
    );
  if (!acc || !form) return <Skeleton active style={{ padding: 24 }} />;

  const set = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));
  const go = (id: string) => {
    setActive(id);
    window.history.replaceState(null, '', `#${id}`);
    document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const joined = new Date(acc.joined_at).toLocaleDateString('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const logoutAll = () =>
    modal.confirm({
      title: 'Выйти на всех устройствах?',
      content:
        'Кабинет закроется везде, включая это устройство. Войти снова можно по email и паролю.',
      okText: 'Выйти везде',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await httpClient.post('/account/logout-all');
          qc.clear();
          navigate('/login', { replace: true });
        } catch (e) {
          message.error(extractErrorMessage(e));
        }
      },
    });

  return (
    <>
      <TopBar
        title="Настройки"
        hint={
          state === 'saving'
            ? 'Сохраняем…'
            : errField
              ? `Не сохранено: ${errField === 'inn' ? 'ИНН' : 'порог уведомления'} — исправьте поле`
              : state === 'saved'
                ? 'Сохранено'
                : state === 'error'
                  ? 'Не сохранилось'
                  : undefined
        }
      />
      <div className="v2-content">
        <div className="ac-grid">
          <nav className="ac-nav" aria-label="Разделы настроек">
            {SECTIONS.map(([id, t]) => (
              <button
                key={id}
                type="button"
                className={active === id ? 'is-on' : undefined}
                aria-current={active === id ? 'true' : undefined}
                onClick={() => go(id)}
              >
                {t}
              </button>
            ))}
          </nav>
          <div className="ac-main">
            <section id="sec-profile" className="v2-card ac-card" aria-labelledby="h-profile">
              <h2 id="h-profile" className="v2-h2">
                Профиль
              </h2>
              <div className="ac-two">
                <label className="ac-field">
                  <span>Имя</span>
                  <input
                    className="st-input"
                    maxLength={100}
                    placeholder="Как к вам обращаться"
                    value={form.display_name}
                    onChange={(e) => set('display_name', e.target.value)}
                  />
                </label>
                <label className="ac-field">
                  <span>Email для входа</span>
                  <input
                    className="st-input"
                    value={acc.email ?? '—'}
                    readOnly
                    aria-readonly="true"
                  />
                </label>
              </div>
              <div className="v2-xs v2-muted">
                В RiChat с {joined.replace(' г.', '')}. Сменить email для входа можно через
                поддержку.
              </div>
            </section>

            <section id="sec-company" className="v2-card ac-card" aria-labelledby="h-company">
              <div>
                <h2 id="h-company" className="v2-h2">
                  Компания и документы
                </h2>
                <div className="v2-xs v2-muted">
                  Нужно для оплаты по счёту и закрывающих документов.
                </div>
              </div>
              <div className="ac-two">
                <label className="ac-field">
                  <span>Название</span>
                  <input
                    className="st-input"
                    maxLength={255}
                    placeholder="ООО или ИП"
                    value={form.company_name}
                    onChange={(e) => set('company_name', e.target.value)}
                  />
                </label>
                <label className="ac-field">
                  <span>ИНН</span>
                  <input
                    className={errField === 'inn' ? 'st-input is-err' : 'st-input'}
                    inputMode="numeric"
                    maxLength={14}
                    placeholder="10 или 12 цифр"
                    value={form.company_inn}
                    aria-invalid={errField === 'inn'}
                    onChange={(e) => set('company_inn', e.target.value)}
                  />
                  {errField === 'inn' && (
                    <span className="ch-err">10 цифр у организации или 12 цифр у ИП</span>
                  )}
                </label>
              </div>
            </section>

            <section id="sec-notifications" className="v2-card ac-card" aria-labelledby="h-notif">
              <h2 id="h-notif" className="v2-h2">
                Уведомления
              </h2>
              <div className="ac-notif-head v2-xs v2-muted">
                <span />
                <span>Почта</span>
              </div>
              <div className="ac-notif">
                <div>
                  <div className="ac-notif-title">Баланс заканчивается</div>
                  <div className="v2-xs v2-muted ac-thr">
                    <label htmlFor="ac-thr">Напомнить, когда останется меньше</label>
                    <input
                      id="ac-thr"
                      className={
                        errField === 'threshold'
                          ? 'st-input ac-thr-input is-err'
                          : 'st-input ac-thr-input'
                      }
                      inputMode="numeric"
                      value={form.low_balance_threshold}
                      disabled={!form.notify_low_balance}
                      aria-invalid={errField === 'threshold'}
                      onChange={(e) =>
                        set('low_balance_threshold', e.target.value.replace(/[^\d\s]/g, ''))
                      }
                    />
                    ₽
                  </div>
                  {errField === 'threshold' && <div className="ch-err">От 0 до 100 000 ₽</div>}
                </div>
                <input
                  type="checkbox"
                  aria-label="Баланс заканчивается — письмо на почту"
                  checked={form.notify_low_balance}
                  onChange={(e) => set('notify_low_balance', e.target.checked)}
                />
              </div>
              <div className="ac-notif">
                <div>
                  <div className="ac-notif-title">Модель ассистента заменена</div>
                  <div className="v2-xs v2-muted">
                    Если разработчик снял модель, мы переключим на запасную и напишем вам
                  </div>
                </div>
                <input
                  type="checkbox"
                  aria-label="Модель ассистента заменена — письмо на почту"
                  checked={form.notify_model_replaced}
                  onChange={(e) => set('notify_model_replaced', e.target.checked)}
                />
              </div>
              <div className="v2-xs v2-muted">
                Когда баланс станет нулевым, письмо придёт всегда — иначе ассистенты замолчат
                незаметно.
              </div>
            </section>

            <section id="sec-security" className="v2-card ac-card ac-sec" aria-labelledby="h-sec">
              <h2 id="h-sec" className="v2-h2">
                Безопасность
              </h2>
              <span style={{ flex: 1 }} />
              <PasswordButton hasPassword={acc.has_password} />
              <button type="button" className="v2-btn" onClick={logoutAll}>
                Выйти на всех устройствах
              </button>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

function PasswordButton({ hasPassword }: { hasPassword: boolean }) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [rep, setRep] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setOpen(false);
    setCur('');
    setNext('');
    setRep('');
    setErr(null);
  };
  const submit = async () => {
    if (next.length < 8) return setErr('Новый пароль — не короче 8 символов');
    if (next !== rep) return setErr('Пароли не совпадают');
    setBusy(true);
    setErr(null);
    try {
      await httpClient.post('/account/password', { current: cur, new: next });
      message.success('Пароль изменён');
      close();
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  if (!hasPassword) return null;
  return (
    <>
      <button type="button" className="v2-btn" onClick={() => setOpen(true)}>
        Сменить пароль
      </button>
      <Modal
        open={open}
        title="Сменить пароль"
        onCancel={close}
        onOk={submit}
        okText="Сменить"
        cancelText="Отмена"
        confirmLoading={busy}
        destroyOnHidden
      >
        <div className="ac-pw">
          <label className="ac-field">
            <span>Текущий пароль</span>
            <input
              className="st-input"
              type="password"
              autoComplete="current-password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
            />
          </label>
          <label className="ac-field">
            <span>Новый пароль</span>
            <input
              className="st-input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </label>
          <label className="ac-field">
            <span>Повторите новый пароль</span>
            <input
              className="st-input"
              type="password"
              autoComplete="new-password"
              value={rep}
              onChange={(e) => setRep(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </label>
          {err && (
            <div role="alert" className="ch-err">
              {err}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
